import ArrSVG from '@public/icons/btn-arr.svg';
import classNames from 'classnames';
import { LinkProps } from 'next/link';
import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import { formatHref, handleOpenPopupFromHref, isSamePath } from '@/shared/hooks/use-open-popup-from-get-params';
import Link from '../Link';
import Spinner from '../Spinner';

export type Props = ButtonHTMLAttributes<HTMLButtonElement> &
    Partial<LinkProps> & {
        /**
         * Какой тег рендерить
         */
        tag?: keyof JSX.IntrinsicElements | typeof Link;
        /**
         * Геометрия кнопки
         */
        geometryVariant?: 'default' | 'wide';
        /**
         * Размер кнопки
         */
        size?: 'md' | 'lg';
        /**
         * Цветовая тема кнопки
         */
        variant?:
            | 'outline-dark'
            | 'outline-light'
            | 'outline-light-filled'
            | 'primary'
            | 'dark-filled'
            | 'primary-filled'
            | 'outline-primary'
            | 'blue'
            | 'dark-blue'
            | 'white'
            | 'grey-rounded-checkable'
            | 'grey-filled';
        target?: string;
        asyncActionActive?: boolean;
        icon?: ReactNode;
    };

const Button = forwardRef<HTMLButtonElement, Props>(
    (
        {
            children,
            variant = 'primary',
            tag = 'button',
            geometryVariant = 'default',
            size = 'md',
            asyncActionActive = false,
            className: propsClassName,
            ...props
        },
        ref,
    ) => {
        const Component = tag;
        const { openPopup } = useSetOpenedPopupsState();
        // Выделяем классы, начинающиеся с "text-"
        const textClasses = propsClassName
            ?.split(' ')
            .filter((cls) => /^text-/.test(cls))
            .join(' ');

        // Остальные классы (не начинающиеся с "text-")
        const otherClasses = propsClassName
            ?.split(' ')
            .filter((cls) => !/^text-/.test(cls))
            .join(' ');

        const handleClick = (e: React.MouseEvent<HTMLElement>) => {
            if (props.href && Component === Link) {
                // Преобразуем href в строку
                const formattedHref = typeof props.href === 'string' ? props.href : formatHref(props.href);

                // Проверяем, совпадает ли путь
                const isSame = isSamePath(formattedHref);

                if (isSame) {
                    e.preventDefault(); // Отменяем переход
                    handleOpenPopupFromHref(formattedHref, openPopup);
                }
            }

            // Если есть onClick в props, вызываем его
            if (props.onClick) {
                props.onClick(e as any);
            }
        };

        return (
            <Component
                {...props}
                ref={ref}
                className={classNames(
                    'btn',
                    `btn-${variant}`,
                    `btn-geometry-${geometryVariant}`,
                    `btn-size-${size}`,
                    otherClasses,
                    {
                        'is-async-action-active': asyncActionActive,
                    },
                )}
                onClick={handleClick} // Добавляем обработчик клика
            >
                {!['primary', 'white', 'blue', 'grey-rounded-checkable', 'grey-filled'].includes(variant) && (
                    <ArrSVG className="btn__arrow" />
                )}
                <span className={classNames('btn__text', textClasses)}>{children}</span>
                {asyncActionActive && <Spinner />}
            </Component>
        );
    },
);

Button.displayName = 'Button';

export default Button;
